import { Link, useParams } from "react-router-dom";
import { assetUrl } from "../api/client";
import { useClip } from "../api/clips";
import ClipStatusBadge from "../components/ClipStatusBadge";
import { formatBytes, formatDate } from "../lib/format";

// Clip preview: the delivered mp4 played straight from the bucket (ranged streaming, so
// seeking works), with the clip's facts and the zip download alongside.

function fmt(ms: number): string {
  const s = Math.round(ms / 1000);
  return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, "0")}`;
}

export default function ClipViewPage() {
  const { id } = useParams<{ id: string }>();
  const clipId = Number(id);
  const { data: clip, isLoading, error } = useClip(clipId);

  if (Number.isNaN(clipId)) return <p className="error">Invalid clip id.</p>;

  if (isLoading) {
    return (
      <div className="clip-view-page">
        <div className="skeleton viewer-skeleton" />
      </div>
    );
  }

  if (error || !clip) {
    return (
      <div className="empty-state">
        <p className="error">Clip not found.</p>
        <Link to="/clips" className="btn">
          ← Back to clips
        </Link>
      </div>
    );
  }

  const start = clip.snapped_start_ms ?? clip.start_ms;
  const end = clip.snapped_end_ms ?? clip.end_ms;
  const durMs = clip.duration_ms ?? clip.end_ms - clip.start_ms;

  return (
    <div className="clip-view-page">
      <header className="detail-header">
        <Link to="/clips" className="crumb">
          ← Clips
        </Link>
        <div className="detail-title-row">
          <h1>{clip.task_title ?? clip.task_id}</h1>
          <div className="detail-tags">
            <ClipStatusBadge status={clip.status} />
          </div>
        </div>
        <div className="detail-subline muted">
          <span>
            Original recording{" "}
            <Link className="mono" to={`/r/${clip.recording_id}`}>
              {clip.recording_log_id}
            </Link>
          </span>
          <span>·</span>
          <span title="Window in the original recording">
            {fmt(start)}–{fmt(end)}
          </span>
          <span>·</span>
          <span>{fmt(durMs)} long</span>
          {clip.size_bytes != null && (
            <>
              <span>·</span>
              <span>{formatBytes(clip.size_bytes)}</span>
            </>
          )}
          <span>·</span>
          <span title={clip.created_by ?? undefined}>created {formatDate(clip.created_at)}</span>
        </div>
        {clip.error && <p className="error detail-error">Error: {clip.error}</p>}
      </header>

      <section className="viewer card">
        {clip.video_url ? (
          <div className="video-frame">
            <video
              className="review-video"
              controls
              playsInline
              preload="metadata"
              src={assetUrl(clip.video_url)}
            />
          </div>
        ) : (
          <div className="viewer-empty">
            {clip.status === "ready"
              ? "This clip has no preview video (built before previews existed). Download the zip to watch it."
              : clip.status === "failed"
                ? "The clip build failed; there is no video to preview."
                : "The clip is still being built. This page refreshes on its own."}
          </div>
        )}
      </section>

      <div className="clip-view-actions">
        {clip.download_url && (
          <a className="btn primary" href={assetUrl(clip.download_url)}>
            Download zip
          </a>
        )}
        <Link className="btn ghost" to={`/r/${clip.recording_id}`}>
          Open original recording
        </Link>
      </div>
    </div>
  );
}
